import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RegionActions } from './RegionActions';
import { RegionList } from './RegionList';
import { RegionCreateModal } from './modals/RegionCreateModal';
import { RegionEditModal } from './modals/RegionEditModal';
import { useRegionsWithCounts, useDeleteRegion } from '@/hooks/useRegions';
import { useRegionStore } from '@/stores/regionStore';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Region } from '@/features/regions/types';

export function RegionsPage() {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const [regionToDelete, setRegionToDelete] = React.useState<Region | null>(null);
  
  // Convert providerId to number with validation
  const providerIdNumber = providerId ? Number(providerId) : null;
  
  // Fetch regions with version counts with proper validation
  const { data: regions = [], isLoading, isError } = useRegionsWithCounts(providerIdNumber || 0);
  
  // Delete region mutation
  const { mutate: deleteRegion } = useDeleteRegion();
  
  // Region store for modals
  const { openCreateModal, openEditModal } = useRegionStore();
  
  // Handle navigation to view versions for a region
  const handleViewVersions = (region: Region) => {
    if (!providerIdNumber || !region || !region.id) return;
    navigate(`/providers/${providerIdNumber}/regions/${region.id}/versions`);
  };
  
  // Handle region deletion
  const handleDeleteRegion = (region: Region) => {
    if (!region) return;
    setRegionToDelete(region);
  };
  
  const confirmDelete = () => {
    if (!regionToDelete || !regionToDelete.id || !providerIdNumber) return;
    
    deleteRegion(
      { id: regionToDelete.id, providerId: providerIdNumber },
      {
        onSuccess: () => {
          setRegionToDelete(null);
        },
        onError: (error: any) => {
          // Handle specific error cases
          if (error.code === 'HAS_VERSIONS') {
            toast.error('This region has associated versions and cannot be deleted.');
          }
        }
      }
    );
  };
  
  // Show error state if no provider ID
  if (!providerIdNumber) {
    return <div className="py-8 text-center text-red-500">Invalid provider ID</div>;
  }
  
  if (isLoading) {
    return <div className="py-8 text-center">Loading regions...</div>;
  }
  
  if (isError) {
    return <div className="py-8 text-center text-red-500">Error loading regions</div>;
  }
  
  return (
    <div className="container mx-auto py-6">
      <RegionActions 
        providerId={providerIdNumber} 
        onAddRegion={openCreateModal} 
      />
      
      <RegionList 
        regions={regions || []} 
        onViewVersions={handleViewVersions}
        onEditRegion={openEditModal}
        onDeleteRegion={handleDeleteRegion}
      />
      
      {/* Modals */}
      <RegionCreateModal providerId={providerIdNumber} />
      <RegionEditModal />
      
      {/* Delete confirmation */}
      <AlertDialog open={!!regionToDelete} onOpenChange={(open) => !open && setRegionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the region "{regionToDelete?.name}".
              {regionToDelete?.zip_code && (
                <span className="block mt-2">
                  This region has zip code: <strong>{regionToDelete.zip_code}</strong>
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
